import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSellerPayments } from '../hooks/queries/sellerQueries';
import { DollarSign, Search, Calendar, Filter } from 'lucide-react';
import { Button, Input } from '../components/ui';

const PAGE_SIZE = 20;

interface PaidOrderItem {
  id?: string;
  product?: { name?: string; images?: string[] };
  quantity?: number;
  price?: number;
  sku?: string;
}

interface PaidOrder {
  id: string;
  orderNumber: string;
  paymentMethod?: string;
  sellerAmount?: number;
  totalAmount?: number;
  items?: PaidOrderItem[];
  user?: {
    firstName?: string;
    lastName?: string;
    email?: string;
  } | null;
  deliveredAt?: string;
  paidAt?: string;
  createdAt?: string;
}

const Payments: React.FC = () => {
  const { t } = useTranslation(['payments', 'common']);
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({ from: '', to: '' });

  const { data: paymentsData, isLoading } = useSellerPayments({
    page,
    limit: PAGE_SIZE,
    search: searchQuery || undefined,
    dateFrom: filters.from || undefined,
    dateTo: filters.to || undefined,
  });

  const orders = (paymentsData?.orders || []) as PaidOrder[];
  const totalPages = paymentsData?.totalPages ?? 1;
  const total = paymentsData?.total ?? 0;
  const totalRevenue = paymentsData?.summary?.totalRevenue ?? 0;

  useEffect(() => {
    const timer = setTimeout(() => setSearchQuery(searchInput.trim()), 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [searchQuery, filters.from, filters.to]);

  const getCustomerName = (order: PaidOrder) => {
    if (!order.user) return t('fields.customer');
    const name = `${order.user.firstName || ''} ${order.user.lastName || ''}`.trim();
    return name || order.user.email || t('fields.customer');
  };

  const formatDate = (value?: string) => {
    if (!value) return '—';
    return new Date(value).toLocaleDateString();
  };

  const rangeStart = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(page * PAGE_SIZE, total);
  const hasActiveFilters = Boolean(searchQuery || filters.from || filters.to);

  return (
    <div className="space-y-4 md:space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">{t('title')}</h1>
          <p className="text-sm md:text-base text-gray-600 mt-1 md:mt-2">{t('subtitle')}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="card p-4 md:p-6">
          <p className="text-xs md:text-sm text-gray-600 mb-1">{t('summary.totalRevenue')}</p>
          <p className="text-2xl md:text-3xl font-bold text-gray-900">
            ${totalRevenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </p>
          <p className="text-xs text-gray-500 mt-1">{t('summary.deliveredAndPaid')}</p>
        </div>
        <div className="card p-4 md:p-6 flex items-center justify-between">
          <div>
            <p className="text-xs md:text-sm text-gray-600 mb-1">{t('summary.paidOrders')}</p>
            <p className="text-2xl md:text-3xl font-bold text-gray-900">{total}</p>
          </div>
          <div className="bg-green-500 p-3 md:p-4 rounded-lg">
            <DollarSign size={24} className="text-white md:w-8 md:h-8" />
          </div>
        </div>
      </div>

      <div className="card p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="relative lg:col-span-2">
            <Search
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              size={20}
            />
            <input
              type="text"
              placeholder={t('search')}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="input-field pl-10 w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('fromDate')}</label>
            <Input
              type="date"
              value={filters.from}
              onChange={(e) => setFilters((f) => ({ ...f, from: e.target.value }))}
              icon={Calendar}
              iconPosition="left"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('toDate')}</label>
            <Input
              type="date"
              value={filters.to}
              onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value }))}
              icon={Calendar}
              iconPosition="left"
            />
          </div>
          <div className="flex items-end lg:col-span-4">
            <Button
              variant="outline"
              icon={Filter}
              iconPosition="left"
              onClick={() => {
                setSearchInput('');
                setSearchQuery('');
                setFilters({ from: '', to: '' });
                setPage(1);
              }}
              disabled={!hasActiveFilters && !searchInput}
              className="w-full sm:w-auto"
            >
              {t('buttons.clearFilters', { ns: 'common' })}
            </Button>
          </div>
        </div>
      </div>

      {isLoading && !paymentsData ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-custom-primary" />
        </div>
      ) : orders.length === 0 ? (
        <div className="card p-12 text-center">
          <DollarSign size={48} className="mx-auto text-gray-400 mb-4" />
          <p className="text-gray-500 text-lg">
            {hasActiveFilters ? t('messages.noMatchingPayments') : t('messages.noPaymentsFound')}
          </p>
        </div>
      ) : (
        <>
          <div className="card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[800px]">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      {t('fields.orderNumber')}
                    </th>
                    <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      {t('fields.customer')}
                    </th>
                    <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      {t('fields.products')}
                    </th>
                    <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      {t('fields.delivered')}
                    </th>
                    <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      {t('fields.paid')}
                    </th>
                    <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      {t('fields.amount')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {orders.map((order) => {
                    const productNames = (order.items || [])
                      .map((item) => item.product?.name)
                      .filter(Boolean)
                      .join(', ');

                    return (
                      <tr key={order.id} className="hover:bg-gray-50">
                        <td className="px-3 md:px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          #{order.orderNumber}
                        </td>
                        <td className="px-3 md:px-6 py-4 text-sm text-gray-700">
                          <div>{getCustomerName(order)}</div>
                          {order.user?.email && (
                            <div className="text-xs text-gray-500">{order.user.email}</div>
                          )}
                        </td>
                        <td className="px-3 md:px-6 py-4 text-sm text-gray-600 max-w-xs truncate">
                          {productNames || '—'}
                        </td>
                        <td className="px-3 md:px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {formatDate(order.deliveredAt)}
                        </td>
                        <td className="px-3 md:px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {formatDate(order.paidAt)}
                        </td>
                        <td className="px-3 md:px-6 py-4 whitespace-nowrap text-sm font-semibold text-green-700">
                          ${(order.sellerAmount ?? order.totalAmount ?? 0).toFixed(2)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {total > 0 && (
            <div className="card p-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="text-sm text-gray-500">
                  {t('table.showing', { ns: 'common' })} {rangeStart} {t('table.to', { ns: 'common' })}{' '}
                  {rangeEnd} {t('table.of', { ns: 'common' })} {total}{' '}
                  {t('table.results', { ns: 'common' })}
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="text-sm py-1 px-2"
                  >
                    {t('table.previous', { ns: 'common' })}
                  </Button>
                  <span className="text-sm text-gray-700">
                    {t('table.page', { ns: 'common' })} {page} {t('table.of', { ns: 'common' })}{' '}
                    {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages}
                    className="text-sm py-1 px-2"
                  >
                    {t('table.next', { ns: 'common' })}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Payments;
